import {
    type CommunityDriveFolder,
    type CommunityProperties,
    CommunityType,
    type LearningSettings,
    RenderingType,
} from '@lumapps/communities/types';
import { ContentTypes } from '@lumapps/content-types/types';
import type { ReadModeContent } from '@lumapps/contents/types';
import type { Tag } from '@lumapps/folksonomy/types';
import type { FocusPoint } from '@lumapps/lumx/react';
import type { PostStatus } from '@lumapps/posts/types';
import type { Tag as PostCategory } from '@lumapps/tags/types';
import type { TranslateObject } from '@lumapps/translations';
import type { MinimumViableUser, User } from '@lumapps/user/types';
import type { PostType } from '@lumapps/widget-post-list/types';

/**
 * List of subviews available for a space.
 * All the views should be listed here.
 * */
export enum SpaceViews {
    article = 'article',
    articles = 'articles',
    calendar = 'calendar',
    content = 'content',
    documents = 'documents',
    drive = 'drive',
    event = 'event',
    events = 'events',
    feed = 'feed',
    home = 'home',
    learning = 'learning',
    posts = 'posts',
    post = 'post',
    search = 'search',
}

export interface SpaceRouteParameters {
    id: string;
    slug: string;
    view?: SpaceViews;
    identifier?: string;
}

export interface SpaceLayoutParameters {
    id: string;
    view?: SpaceViews;
}

export enum SpaceRole {
    admin = 'admin',
    member = 'member',
    limitedMember = 'limited_member',
}

export enum SpaceAccessRequestStatus {
    accepted = 'accepted',
    pending = 'pending',
    rejected = 'rejected',
}

export enum SpaceServices {
    article = 'article',
    calendar = 'calendar',
    documents = 'documents',
    drive = 'drive',
    event = 'event',
    feed = 'feed',
    homepage = 'homepage',
    learning = 'learning',
    posts = 'posts',
}

export enum SpaceWebviews {
    homepage = 'homepage',
    calendar = 'calendar',
    settings = 'settings',
}

export enum SpaceResources {
    post = 'post',
    event = 'event',
    article = 'article',
    content = 'content',
}

export enum SpaceVisibility {
    open = 'open',
    read_only = 'read_only',
    private = 'private',
    secret = 'secret',
}

export interface SpaceSubscriptions {
    isNotified: boolean;
    isSubscribed: boolean;
}

export interface SpaceSubscriptionSettings {
    areNotificationsEnabledOnSubscription: boolean;
}

export interface SpacePermissions {
    /** Whether the user can add a post to the space */
    canContribute: boolean;
    /** Whether the user can add an article to the space */
    canCreateArticle: boolean;
    /** Whether the user can add an event to the space */
    canCreateEvent: boolean;
    /** Whether the user can delete the space */
    canDelete: boolean;
    /** Whether the user can edit the space settings */
    canEdit: boolean;
    /** Whether the user can add, delete and rename documents */
    canManageMedia: boolean;
    /** Whether the user can request access to the space (changes accordingly with visibility and their membership) */
    canRequestMembership: boolean;
    /** Whether the user can follow the space (changes accordingly with visibility and their membership and subscription) */
    canSubscribe: boolean;
}

export interface SpaceMembership {
    requestDetails: {
        status: SpaceAccessRequestStatus;
    };
}

/**
 * Define how the Space header should handle the image
 * 'small' = as a thumbnail
 * 'cover' = as a banner
 */
export enum SpaceThumbnailDisplayType {
    small = 'small',
    cover = 'cover',
}

export interface SpaceCoverImage {
    url: string;
    name: string;
    mediaId: string;
    focalPoint: FocusPoint;
}

export interface SpaceGroups {
    technicalGroupId: string;
    adminGroupIds: string[];
    memberGroupIds: string[];
}

export enum SpaceServiceProvider {
    google = 'google',
    lumapps = 'lumapps',
    microsoft = 'microsoft',
}

// Only posts can be pinned for now, but we might add more types in the future
export enum SpacePinnedResourceType {
    post = 'POST',
}

export type SpacePinnedResource = { type: SpacePinnedResourceType; id: string };

export type SpaceServicesProviders = Partial<Record<SpaceServices, SpaceServiceProvider | null>>;

export interface SpaceData {
    id: string;
    slug: TranslateObject;
    title: TranslateObject;
    description: TranslateObject;
    coverImage?: SpaceCoverImage;
    headerDisplayMode: SpaceThumbnailDisplayType;
    enabledServices: SpaceServices[];
    myMembership: SpaceMembership | null;
    myPermissions: SpacePermissions;
    mySubscriptions: SpaceSubscriptions;
    subscriptionSettings: SpaceSubscriptionSettings;
    visibility: SpaceVisibility;
    folksonomyTags?: Tag[];
    spaceGroups: SpaceGroups;
    providers: SpaceServicesProviders;
    postTypes: PostType[];
    pinnedResources: SpacePinnedResource[];
}

export interface SpaceMembersApiOptions {
    role?: SpaceRole;
    searchQuery?: string;
    cursor?: string | null;
    maxResults?: number;
}

export interface SpaceDataApiOptions {
    previewMode?: RenderingType;
}

export interface SpaceUserMember extends MinimumViableUser<'id' | 'fullName'> {
    role: SpaceRole;
}

export interface SpaceMembersApiResponse {
    items: Array<SpaceUserMember>;
    cursor: string | null;
    more: boolean;
}

export type EmptyStateAction = {
    resourceType: string;
    menuItemKey: string;
    singleActionKey: string;
    icon: string;
    onClick(): void;
};

export interface SpaceCalendarSettings {
    name: string;
    source: SpaceServiceProvider;
}

export interface SpaceSelectedCalendar extends SpaceCalendarSettings {
    id: string;
}

export interface SpaceViewParams extends Record<string, string | undefined> {
    view?: SpaceViews;
    // Space id
    id: string;
    // Space slug
    slug: string;
    // id of the view detail (post, event or article)
    identifier?: string;
}

export enum SpaceLegacyPrivacy {
    restricted = 'restricted', // secret
    descriptionOnly = 'description_only', // private
    readOnly = 'read_only', // members-only contribution
    open = 'open', // open
}

export interface SpaceLegacyData extends ReadModeContent {
    /** Content type is community even for a space (yes, I know...) */
    type: typeof ContentTypes.COMMUNITY;
    /** Array containing all the USERS manually picked as administrators */
    adminKeys: string[];
    /** Whether the user is a member of the space */
    canContribute: boolean;
    /** Whether the user can edit the space */
    canEdit: boolean;
    /** Whether the user can delete the space */
    canDelete: boolean;
    /** Canonical url of the space */
    canonicalUrl: { translations: Record<string, never> };
    /** This key existed to support the chat in communities */
    chat?: null;
    /** Feed key referring to GROUPS manually picked as editors (administrators) and contributors (advanced users) */
    communityFeedKey: string;
    /** Settings used for the calendar service in spaces */
    calendarSettings?: SpaceCalendarSettings;
    /** All spaces are of community type email */
    communityType: typeof CommunityType.email;
    /** Space description */
    description: Record<string, string>;
    /** Array containing all the GROUPS manually picked as administrators */
    editors: string[];
    /** The feedKeys array contains the communityFeedKey which is the feed key referring to GROUPS picked as
     * administrators (editors), advanced members (contributors) and members (limited members).
     * It is returned by the backend and should not be manually changed when saving (use editors / publishers arrays) */
    feedKeys: string[];
    /** Use this as a fallback, since it is the old id field */
    id: string;
    /** Instance the space belongs to (aka site) */
    instance: string;
    /** Whether the space has a calendar */
    hasCalendarId: boolean;
    /** The calendar's id */
    calendarId?: string;
    /** Has a dedicated chat */
    hasChat: false;
    /** Whether the article service is enabled */
    hasArticlesEnabled: boolean;
    /** Whether the event service is enabled */
    hasEventsEnabled: boolean;
    /** Whether the document-library is enabled */
    hasManagedMediaLibraryEnabled?: boolean;
    /** Whether the feed is enabled, for space only */
    hasSpaceFeedEnabled?: boolean;
    /** Whether posts are enabled, for space only */
    hasPostsEnabled?: boolean;
    /** Whether the space has a dedicated drive folder */
    hasDriveFolder: boolean;
    /** Whether the lumapps events are displayed in the calendar */
    hasEventsInCalendarEnabled?: boolean;
    /** Selected drive folder */
    driveFolder?: CommunityDriveFolder;
    /** Number of members of the space */
    membersCount?: number;
    /** Array of enabled statuses for idea posts */
    postStatuses?: Array<PostStatus>;
    /** Array of available posts types for the given community. */
    postTypes: PostType[];
    /** Space privacy (aka visibility) */
    privacy: SpaceLegacyPrivacy;
    /** Various properties such as whether the home or the learning service are enabled */
    properties: CommunityProperties;
    /** Array containing all the groups manually picked as members */
    publishers: string[];
    /** Array containing all the groups manually picked as limited members */
    limitedUserFeedKeys: string[];
    /** @deprecated Post's medias are accessible outside the space or not (we should use the FF "security at community level" instead) */
    securedRepository: boolean;
    /** List of feeds which are forced to follow */
    subscribedFeedKeys?: string[];
    /** Slug of the space, used to build its URL */
    slug: TranslateObject;
    /** Thumbnail displayed in the space header */
    thumbnail?: string;
    /** The way the thumbnail gets displayed in the space header */
    thumbnailDisplayType: SpaceThumbnailDisplayType;
    /** Title of the space */
    title: TranslateObject;
    /** Uid of the space */
    uid: string;
    /** The date this space has been last updated */
    updatedAt?: string;
    /** The details of the user that updated the space */
    updatedByDetails?: User;
    /** Array containing all the users manually picked as members */
    userKeys: string[];
    userDetails: User[];
    /** Array containing all the users manually picked as limited members */
    limitedUserKeys: string[];
    limitedUserDetails: User[];
    workspace: string;
    /** Template (used only in communities) */
    template: undefined;
    /** Tagz linked to the space (aka folksonomy tags) */
    tagz?: Tag[];
    /** Posts categories details */
    tagsDetails?: Array<PostCategory>;
    /** Posts categories ids */
    tags?: Array<string>;
    /** Whether we are dealing with a space or a community */
    renderingType: RenderingType.space;
    /** Configuration object for the learning service */
    learningSettings?: LearningSettings;
}
